/**
 * Demanda planejada e herança de escopo.
 *
 * Hierarquia real do DATAFLOW nesta tela: departamento > organização.
 * O filtro "unidade" do Painel é org_departments. Não há entidade de unidade
 * separada no fluxo operacional, então não existe terceiro nível.
 *
 * Configuração herdada não é copiada. A resolução escolhe a linha vigente
 * mais específica.
 *
 * standardMinutesPerTask existe só como reserva. Lead time, sizing_hours,
 * tempo observado, SLA e takt não preenchem esse campo.
 */

export const DEMAND_UNIT = {
  tasks_per_hour: 'tarefas/h',
} as const;

export type DemandUnit = keyof typeof DEMAND_UNIT;

export type DemandScopeSource = 'department' | 'organization';

export interface PlannedDemandRecord {
  id: string;
  organizationId: string;
  departmentId: string | null;
  value: number;
  unit: DemandUnit;
  /** Reserva. Não entra no cálculo desta rodada. */
  standardMinutesPerTask: number | null;
  validFrom: string;
  validTo: string | null;
  active: boolean;
}

export interface ResolvedPlannedDemand {
  value: number;
  unit: DemandUnit;
  source: DemandScopeSource;
  recordId: string;
}

export function localDateIso(date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function demandUnitLabel(unit: DemandUnit): string {
  return DEMAND_UNIT[unit];
}

export function isDemandInForce(record: PlannedDemandRecord, onDate: string): boolean {
  if (!record.active) return false;
  if (record.validFrom > onDate) return false;
  if (record.validTo != null && record.validTo < onDate) return false;
  return true;
}

function latestInForce(records: PlannedDemandRecord[]): PlannedDemandRecord | null {
  return [...records].sort((a, b) => {
    const byStart = b.validFrom.localeCompare(a.validFrom);
    if (byStart !== 0) return byStart;
    return b.id.localeCompare(a.id);
  })[0] ?? null;
}

/**
 * Departamento com configuração vigente vence a da organização.
 * Sem configuração do departamento, herda a da organização.
 * Linha expirada, futura ou inativa não entra.
 */
export function resolvePlannedDemand(
  records: PlannedDemandRecord[],
  scope: { departmentId: string | null },
  onDate: string,
): ResolvedPlannedDemand | null {
  const inForce = records.filter((record) => isDemandInForce(record, onDate));
  if (scope.departmentId) {
    const department = latestInForce(inForce.filter((record) => record.departmentId === scope.departmentId));
    if (department) {
      return {
        value: department.value,
        unit: department.unit,
        source: 'department',
        recordId: department.id,
      };
    }
  }
  const organization = latestInForce(inForce.filter((record) => record.departmentId == null));
  if (!organization) return null;
  return {
    value: organization.value,
    unit: organization.unit,
    source: 'organization',
    recordId: organization.id,
  };
}

export function demandSourceLabel(source: DemandScopeSource): string {
  return source === 'department' ? 'Configuração do departamento' : 'Herdado da organização';
}
